import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import { QRCodeCanvas } from 'qrcode.react';
import Loader from '../../Loader/Loader';
import '../../../styles/components/StoreCheckInPage.scss';

const StoreCheckInPage: React.FC = () => {
  const { hash_id } = useParams<{ hash_id: string }>();
  const [store, setStore] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter used for closing picker
  const [, setShowColorPicker] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [showKioskControls, setShowKioskControls] = useState(false);
  const [isScreenCasting, setIsScreenCasting] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [castingError, setCastingError] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState(1);


  useEffect(() => {
    // Check for fullscreen/kiosk mode from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const fullscreen = urlParams.get('fullscreen');
    const kiosk = urlParams.get('kiosk');
    
    setIsFullscreen(fullscreen === 'true');
    setIsKioskMode(kiosk === 'true');

    // Hide navbar and other UI elements in kiosk mode
    if (kiosk === 'true') {
      const navbar = document.querySelector('.navbar') as HTMLElement;
      const sidenav = document.querySelector('.sidenav') as HTMLElement;
      const mainContent = document.querySelector('.main-content') as HTMLElement;
      
      if (navbar) navbar.style.display = 'none';
      if (sidenav) sidenav.style.display = 'none';
      if (mainContent) mainContent.style.marginLeft = '0';
      
      // Hide browser UI elements
      document.body.style.overflow = 'hidden';
      
      // Auto-enter fullscreen if supported
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {
          // Fullscreen failed, continue without it
        });
      }
    }

    const fetchStore = async () => {
      if (!hash_id) return;

      const { data: storeData } = await supabase
        .from('stores')
        .select('*, clients(name, logo_url)')
        .eq('hash_id', hash_id)
        .eq('is_active', true)
        .single();

      if (storeData) {
        setStore(storeData);
      }
      setIsLoading(false);
    };

    fetchStore();
  }, [hash_id]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowColorPicker(null);
        // Exit fullscreen if in kiosk mode
        if (isKioskMode && document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isKioskMode]);

  // Handle kiosk controls visibility with timeout
  useEffect(() => {
    if (!isKioskMode) return;

    const handleMouseMove = () => {
      setShowKioskControls(true);
      
      // Hide controls after 3 seconds of no movement
      const timeout = setTimeout(() => {
        setShowKioskControls(false);
      }, 3000);

      return () => clearTimeout(timeout);
    };

    const timeout = setTimeout(() => {
      setShowKioskControls(false);
    }, 3000);

    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isKioskMode]);

  // Screen casting functions
  const triggerNativeCasting = async () => {
    setCastingError('');
    
    try {
      // Method 1: Try to trigger the browser's native casting UI
      if ('presentation' in navigator) {
        // This triggers the native casting dialog
        const presentationRequest = new (window as any).PresentationRequest([
          'https://app.vivvy.ai/checkin/' + hash_id + '?kiosk=true'
        ]);
        
        const connection = await presentationRequest.start();
        setIsScreenCasting(true);
        
        connection.addEventListener('close', () => {
          setIsScreenCasting(false);
        });
        
        return;
      }

      // Method 2: Try to use the Cast API if available
      if ((window as any).chrome && (window as any).chrome.cast) {
        await new Promise((resolve, reject) => {
          (window as any).chrome.cast.requestSession(resolve, reject);
        });
        setIsScreenCasting(true);
        return;
      }

      // Method 3: Fallback - try to trigger the browser's cast menu
      // This simulates the right-click "Cast" action
      const castButton = document.createElement('button');
      castButton.style.display = 'none';
      castButton.setAttribute('data-cast', 'true');
      document.body.appendChild(castButton);
      
      // Try to trigger the browser's native casting
      if ('mediaSession' in navigator) {
        (navigator as any).mediaSession.setActionHandler('previoustrack', () => {
          // This might trigger casting in some browsers
        });
      }

      // Method 4: Show instructions for manual casting
      setCastingError('Please use your browser\'s native casting feature:\n\n1. Right-click on this page\n2. Select "Cast" or "Send to device"\n3. Choose your casting device');
      
    } catch (err: any) {
      console.error('Native casting error:', err);
      
      // Fallback: Show manual instructions
      setCastingError('To cast this page:\n\n1. Right-click anywhere on this page\n2. Select "Cast" or "Send to device"\n3. Choose your Chromecast, AirPlay, or other device\n\nOr use your browser\'s cast button in the address bar.');
    }
  };



  const stopScreenCasting = () => {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
    }
    setIsScreenCasting(false);
    setCastingError('');
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2)); // Max zoom 200%
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5)); // Min zoom 50%
  };

  const resetZoom = () => {
    setZoomLevel(1);
  };

  if (isLoading) return <Loader />;
  if (!store) return <div>Store not found</div>;

  const designConfig = store.design_config || {};
  const client = store.clients;

  const getOverlayStyle = () => {
    if (designConfig.overlayMode === "gradient") {
      return `linear-gradient(135deg, ${designConfig.gradientStart || 'rgba(51, 161, 181, 0.8)'}, ${designConfig.gradientEnd || 'rgba(104, 219, 184, 0.8)'})`;
    }
    return designConfig.solidColor || 'rgba(51, 161, 181, 0.8)';
  };

  const getBackgroundSource = () => {
    return designConfig.bgUrl || 'https://w0.peakpx.com/wallpaper/761/22/HD-wallpaper-blue-background-1-computer-technology-art-background-painting-wide-screen-computer-graphics-abstract-artwork.jpg';
  };

  const getLogoSource = () => {
    return designConfig.logoUrl || client?.logo_url || '';
  };

  return (
    <div className={`store-checkin-page ${isKioskMode ? 'kiosk-mode' : ''} ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Background Image/Video */}
      <div className="background-media">
        <img
          src={getBackgroundSource()}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Overlay */}
      <div
        className="overlay"
        style={{
          background: getOverlayStyle(),
        }}
      >
        <div 
          className="checkin-content"
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease'
          }}
        >
          {/* Logo */}
          {getLogoSource() && (
            <img
              src={getLogoSource()}
              alt="Logo"
              className="store-logo"
              style={{
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
              }}
            />
          )}

          {/* Headline */}
          <h1
            className="headline"
            style={{
              color: designConfig.fontColor || '#ffffff',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            {designConfig.headline || 'Welcome! Scan to check in'}
          </h1>

          {/* QR Code */}
          <div className="qr-container">
            <QRCodeCanvas
              value={`https://app.vivvy.ai/chatbot/${hash_id}`}
              size={200}
              fgColor={designConfig.qrColor || '#ffffff'}
              bgColor="transparent"
              style={{
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
              }}
            />
          </div>

          {/* Powered by */}
          <p
            className="powered-by"
            style={{
              color: designConfig.fontColor || '#ffffff',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            Powered by Vivvy
          </p>

          {/* Store Name */}
          <p
            className="store-name"
            style={{
              color: designConfig.fontColor || '#ffffff',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            {store.store_name}
          </p>
        </div>
      </div>

      {/* Kiosk Mode Controls */}
      {isKioskMode && (
        <div className={`kiosk-controls ${showKioskControls ? 'visible' : 'hidden'}`}>
          <button
            className="kiosk-btn"
            onClick={() => {
              if (document.fullscreenElement) {
                document.exitFullscreen();
              } else {
                document.documentElement.requestFullscreen();
              }
            }}
          >
            {document.fullscreenElement ? '📱 Exit Fullscreen' : '🖥️ Fullscreen'}
          </button>
          <button
            className={`kiosk-btn cast-btn ${isScreenCasting ? 'casting' : ''}`}
            onClick={isScreenCasting ? stopScreenCasting : triggerNativeCasting}
            title={isScreenCasting ? 'Stop Screen Casting' : 'Start Screen Casting'}
          >
            {isScreenCasting ? '📺 Stop Casting' : '📺 Cast Screen'}
          </button>
          <button
            className="kiosk-btn"
            onClick={() => window.location.href = `https://app.vivvy.ai/chatbot/${hash_id}`}
          >
            🤖 Open Chatbot
          </button>
        </div>
      )}

      {/* Zoom Controls */}
      {isKioskMode && (
        <div className={`zoom-controls ${showKioskControls ? 'visible' : 'hidden'}`}>
          <div className="zoom-info">
            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
          </div>
          <div className="zoom-buttons">
            <button
              className="zoom-btn"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.5}
              title="Zoom Out"
            >
              🔍−
            </button>
            <button
              className="zoom-btn reset-btn"
              onClick={resetZoom}
              title="Reset Zoom"
            >
              🔄
            </button>
            <button
              className="zoom-btn"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 2}
              title="Zoom In"
            >
              🔍+
            </button>
          </div>
        </div>
      )}

      {/* Screen Casting Error */}
      {castingError && (
        <div className="casting-error">
          <p>❌ {castingError}</p>
          <button onClick={() => setCastingError('')}>✕</button>
        </div>
      )}


    </div>
  );
};

export default StoreCheckInPage;
